import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { X, Loader2, CreditCard, CheckCircle2, ArrowRightLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { BookingWithCourt, BookingPaymentRow, CheckinState } from "@/hooks/useMyBookings";
import { isCustomerRescheduleWindow, canCustomerReschedule } from "@/lib/customerReschedule";

interface BookingActionsProps {
  booking: BookingWithCourt;
  showCancel?: boolean;
  userPayment: BookingPaymentRow | undefined;
  showPayButton: boolean;
  checkin: CheckinState | null;
  payingBookingId: string | null;
  canCancel: (booking: BookingWithCourt) => boolean;
  cancelIsPending: boolean;
  checkinIsPending: boolean;
  onCheckin: (bookingId: string) => void;
  onPayNow: (booking: BookingWithCourt) => void;
  onReschedule: (booking: BookingWithCourt) => void;
  onCancel: (bookingId: string) => void;
}

export function BookingActions({
  booking,
  showCancel,
  userPayment,
  showPayButton,
  checkin,
  payingBookingId,
  canCancel,
  cancelIsPending,
  checkinIsPending,
  onCheckin,
  onPayNow,
  onReschedule,
  onCancel,
}: BookingActionsProps) {
  const { t } = useTranslation('booking');
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* Check-in section */}
      {checkin?.status === "checked_in" && (
        <div className="flex items-center gap-1 text-xs text-green-700 bg-green-100 rounded-full px-2 py-1">
          <CheckCircle2 className="h-3.5 w-3.5" />
          Incheckad kl {format(checkin.time!, "HH:mm")}
        </div>
      )}
      {checkin?.status === "open" && (
        <Button
          size="sm"
          className="gap-1 animate-pulse bg-green-600 hover:bg-green-700 text-white"
          onClick={(e) => { e.stopPropagation(); onCheckin(booking.id); }}
          disabled={checkinIsPending}
        >
          <CheckCircle2 className="h-3.5 w-3.5" />
          Checka in
        </Button>
      )}
      {checkin?.status === "too_early" && (
        <span className="text-xs text-muted-foreground">Check-in öppnar om {checkin.opensIn} min</span>
      )}
      {showPayButton && (
        <>
          <Button
            size="sm"
            className="gap-1"
            onClick={(e) => { e.stopPropagation(); onPayNow(booking); }}
            disabled={payingBookingId === booking.id}
          >
            {payingBookingId === booking.id ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <CreditCard className="h-3.5 w-3.5" />
            )}
            Betala nu
          </Button>
          {userPayment?.deadline && (
            <span className="text-[10px] text-muted-foreground">
              {new Date(userPayment.deadline) > new Date()
                ? `Betala senast ${format(new Date(userPayment.deadline), "d MMM HH:mm", { locale: sv })}`
                : "⚠️ Deadline passerad"}
            </span>
          )}
        </>
      )}
      {userPayment?.status === "overdue" && (
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-destructive">🔴 Betalning krävs</span>
          {userPayment.grace_deadline && (
            <span className="text-[10px] text-muted-foreground">
              Du har till {format(new Date(userPayment.grace_deadline), "d MMM HH:mm", { locale: sv })}
            </span>
          )}
          <Button
            size="sm"
            variant="destructive"
            className="gap-1"
            onClick={(e) => { e.stopPropagation(); onPayNow(booking); }}
            disabled={payingBookingId === booking.id}
          >
            <CreditCard className="h-3.5 w-3.5" />
            Betala nu
          </Button>
        </div>
      )}
      {showCancel && isCustomerRescheduleWindow(booking) && (
        canCustomerReschedule(booking) ? (
          <Button
            variant="outline"
            size="sm"
            className="gap-1"
            onClick={(e) => { e.stopPropagation(); onReschedule(booking); }}
          >
            <ArrowRightLeft className="h-3.5 w-3.5" />
            Byt tid
          </Button>
        ) : (
          // C1-D1: MATCHi-mirrored/pushed → admin-only move during coexistence. Disabled + muted text
          // (touch has no hover, so the explanation is inline rather than in a tooltip).
          // stopPropagation so a tap on the muted explanation doesn't bubble to the card and open the
          // detail modal — matches the desktop <span> guard (parity; drift between surfaces = bug).
          <div className="flex flex-col gap-0.5" onClick={(e) => e.stopPropagation()}>
            <Button variant="outline" size="sm" className="gap-1" disabled>
              <ArrowRightLeft className="h-3.5 w-3.5" />
              Byt tid
            </Button>
            <span className="text-[10px] text-muted-foreground max-w-[180px] leading-tight">
              {t('rescheduleMatchiBlocked')}
            </span>
          </div>
        )
      )}
      {showCancel && booking.status === "confirmed" && (
        <Button
          variant="outline"
          size="sm"
          className="text-destructive"
          onClick={(e) => { e.stopPropagation(); onCancel(booking.id); }}
          disabled={!canCancel(booking) || cancelIsPending}
        >
          <X className="h-3.5 w-3.5" />
          {canCancel(booking) ? "Avboka" : "Kan ej avboka"}
        </Button>
      )}
    </div>
  );
}
